/// <amd-module name="Widgets/CumulativeFlowDiagram/CumulativeFlowDiagramChartPropsProvider" />
import { Column } from "Analytics/Boards/CumulativeFlowDiagramQueries/BoardColumnsQuery";
import { CFDSamplePoint } from "Analytics/Boards/CumulativeFlowDiagramQueries/CumulativeFlowHistoryQuery";
import { onWidgetClick } from "Dashboards/WidgetCommon/WidgetTelemetry";
import { WidgetProps } from "Dashboards/WidgetContracts/WidgetProps";
import { format } from "VSS/Core/Util/String";
import { ChartProps, ChartTypesConstants, ClickEvent, DataSeries, LabelFormatModes } from "VSS/Features/Charts/Contracts";
import { IVssPageContext } from "VSS/Platform/Context";
import { FastPageSwitch } from "VSS/Platform/FPS";
import * as Resources from "Widgets/CumulativeFlowDiagram/Resources";
import { createBoardUrl } from "./BoardsUtils";
import { NamedColorPalette } from "./ChartColorPalettes";
import { dateStringAsDate, daysBetweenDates } from "./DateUtils";

export interface CreateChartPropsArgs {
    /** Title of the chart. Used for accessibility for describing the chart to screen readers. */
    title: string;
    /** The dimensions the chart should occupy. */
    size: { width: number; height: number };
    /** The columns of an agile/kanban board. */
    boardColumns: Column[];
    /** Cumulative flow history samples of an agile/kanban board.  */
    cumulativeFlowHistory: CFDSamplePoint[];
    /**
     * The start date (yyyy-MM-dd) in the account time zone of when the cumulative flow history was sampled.
     * Informs the first x-axis value to show on the chart.
     */
    startDate: string;
    /**
     * The end date (yyyy-MM-dd) in the account time zone of when the cumulative flow history was sampled.
     * Informs the last x-axis value to show on the chart.
     */
    endDate: string;
    /**
     * Indicates whether the cumulative flow history associated with the first board column (lowest column order)
     * should be included in the chart props.
     */
    includeFirstBoardColumn: boolean;

    desiredBoardColumnIds: string[] | undefined;
    /** Indicates whether the chart props returned should tell the chart to animate when drawing. */
    showLoadAnimations: boolean;
    /** The palette used when assigning colors to chart series. */
    colorPalette: NamedColorPalette;
    /** An action to take when the chart is clicked. */
    onChartClick: (event: ClickEvent) => void;
}

/** Responsible for processing cumulative flow data into properties for the charting framework to render a visualization of the data. */
export class CumulativeFlowDiagramChartPropsProvider {
    public constructor(private context: IVssPageContext) {}

    /**
     * Creates props using given options for a Chart component to render a cumulative flow diagram visualization
     * of the given cumulative flow data.
     */
    public createChartProps({
        title,
        size,
        boardColumns,
        cumulativeFlowHistory,
        startDate,
        endDate,
        includeFirstBoardColumn,
        desiredBoardColumnIds,
        showLoadAnimations,
        colorPalette,
        onChartClick
    }: CreateChartPropsArgs): ChartProps {
        const series = this.createChartSeries(
            startDate,
            endDate,
            boardColumns,
            includeFirstBoardColumn,
            desiredBoardColumnIds,
            cumulativeFlowHistory,
            colorPalette
        );

        // Get the lowest value of the bottom series (the Done column) so the series doesn't dominate the chart
        // making it hard to see the series stacked above it.
        const minValue = series.length > 0 ? Math.min(...(series[0].data as number[])) : 0;

        return {
            pageContext: this.context,
            hostOptions: size,
            chartType: ChartTypesConstants.StackedArea,
            title,
            series,
            suppressAnimation: !showLoadAnimations,
            tooltip: {
                onlyShowFocusedSeries: true
            },
            xAxis: {
                renderToEdges: true,
                labelFormatMode: LabelFormatModes.DateTime_DayInMonth,
                labelValues: this.createXAxisLabels(startDate, endDate),
                formatAccessibleLabelsUsingAxisCategories: true
            },
            yAxis: {
                allowDecimals: false,
                endOnTick: false,
                updateMinOnLegendClick: true,
                min: minValue
            },
            click: onChartClick
        };
    }

    public createChartClickHandler(
        {
            project,
            team,
            boardCategoryReferenceName
        }: {
            project: string;
            team: string;
            boardCategoryReferenceName: string;
        },
        widgetProps: WidgetProps
    ): (event: ClickEvent) => void {
        const boardUrl = createBoardUrl(this.context, project, team, boardCategoryReferenceName);
        return (event: ClickEvent) => {
            onWidgetClick(this.context, widgetProps);
            if (widgetProps.openLinksInNewTab || event.ctrlKey || event.button === 2) {
                const newWindow = window.open(boardUrl, "_blank");
                if (newWindow) {
                    newWindow.opener = null;
                }
            } else {
                FastPageSwitch(this.context, boardUrl, true /* recordHistoryEntry */);
            }
        };
    }

    private createChartSeries(
        startDate: string,
        endDate: string,
        boardColumns: Column[],
        includeFirstBoardColumn: boolean,
        desiredBoardColumnIds: string[] | undefined,
        cumulativeFlowHistory: CFDSamplePoint[],
        colorPalette: NamedColorPalette
    ): DataSeries[] {
        const dataPointCount = daysBetweenDates(startDate, endDate) + 1;

        const emptyArray: number[] = [];
        for (let i = 0; i < dataPointCount; ++i) {
            emptyArray.push(0);
        }
        const columnIdToSeriesValues: { [columnId: string]: number[] } = {};

        for (const dataPoint of cumulativeFlowHistory) {
            const index = daysBetweenDates(startDate, dataPoint.SampleDate);

            if (columnIdToSeriesValues[dataPoint.ColumnId] == null) {
                columnIdToSeriesValues[dataPoint.ColumnId] = emptyArray.slice();
            }

            columnIdToSeriesValues[dataPoint.ColumnId][index] = dataPoint.Count;
        }

        const orderedColumns = this.orderBoardColumns(boardColumns);
        const hasFirstBoardColumnValues =
            orderedColumns.length > 0 && columnIdToSeriesValues[orderedColumns[orderedColumns.length - 1].ColumnId] != null;

        const doneColumnHasValues = columnIdToSeriesValues[orderedColumns[0].ColumnId] != null;
        if (desiredBoardColumnIds === undefined && !includeFirstBoardColumn) {
            // When the user has not customized the set of columns:
            // A kanban board requires a leftmost New state column and a rightmost Done state column.
            // Thus orderedColumns will always contain at least the Done state board column and first board column.
            // Due to sorting, the last entry in the array is the first board column.
            orderedColumns.pop();
        }

        const series: DataSeries[] = [];

        // Used for tracking the number of columns (regardless of if we choose to render), for the purpose of consistently assigning colors. This prevents colors from shifting while a user toggles visibility of columns on widget/report configuration.
        let colorIteration = 0;
        for (const column of orderedColumns) {
            if (columnIdToSeriesValues[column.ColumnId] != null) {
                // First series (usually the done/completed series) gets its own color
                // Other series are linearly assigned colors from the other colors list, rotating through the list as necessary
                let seriesColor: string;
                if (colorIteration === 0 && doneColumnHasValues) {
                    seriesColor = colorPalette.doneColor;
                } else {
                    const otherColorsOffset = doneColumnHasValues ? -1 : 0;
                    seriesColor = colorPalette.otherColors[(colorIteration + otherColorsOffset) % colorPalette.otherColors.length];
                }

                const showColumn = desiredBoardColumnIds === undefined || desiredBoardColumnIds.indexOf(column.ColumnId) >= 0;
                if (showColumn) {
                    series.push({
                        name: this.calculateSeriesNameFromColumn(column),
                        data: columnIdToSeriesValues[column.ColumnId],
                        color: seriesColor
                    });
                }
                colorIteration++;
            }
        }

        // The proposed series (if present) gets its own color
        if (
            (includeFirstBoardColumn ||
                (desiredBoardColumnIds !== undefined && desiredBoardColumnIds.indexOf(orderedColumns[orderedColumns.length - 1].ColumnId) >= 0)) &&
            hasFirstBoardColumnValues
        ) {
            series[series.length - 1].color = colorPalette.proposedColor;
        }

        return series;
    }

    private createXAxisLabels(startDateString: string, endDateString: string): Date[] {
        const labelCount = daysBetweenDates(startDateString, endDateString) + 1;

        const labels: Date[] = [];
        for (let i = 0; i < labelCount; ++i) {
            const startDate = dateStringAsDate(startDateString);
            const labelDate = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate() + i);
            labels.push(labelDate);
        }

        return labels;
    }

    private orderBoardColumns(boardColumns: ReadonlyArray<Column>): Column[] {
        const activeColumns: Column[] = [];
        const deletedColumns: Column[] = [];

        for (const column of boardColumns) {
            if (column.IsDeleted) {
                deletedColumns.push(column);
            } else {
                activeColumns.push(column);
            }
        }

        // Order deleted columns by deletion date (latest to earliest)
        deletedColumns.sort((a, b) => {
            const revisedDateA = new Date(a.RevisedDate);
            const revisedDateB = new Date(b.RevisedDate);
            return revisedDateB.getTime() - revisedDateA.getTime();
        });

        // Order active columns by column order (last to first)
        activeColumns.sort((a, b) => b.ColumnOrder - a.ColumnOrder);

        // Insert sorted deleted columns immediately after the Done state column of the ordered active columns
        // [startIndex, deleteCount, ...items]
        const orderedColumns = activeColumns.slice();
        orderedColumns.splice(1, 0, ...deletedColumns);

        return orderedColumns;
    }

    private calculateSeriesNameFromColumn(column: Column): string {
        if (column.IsDeleted) {
            return format(Resources.DeletedColumnName_Format, column.ColumnName);
        } else {
            return column.ColumnName;
        }
    }
}
